'use client';

import type { ChangeEvent, FC, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { SupportUserSummaryWriteRefusalEnum } from '../../services/generated/Api';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { SUPPORT_UPLOAD_LIMIT_BYTES } from '../../queries/useSupportQueries';
import { FaPlus, FiX, LuCamera } from '../icons';
import { cn } from '../../utils/cn';
import translations from './SupportChat.translations';

interface SupportComposerProps {
  canWrite: boolean;
  refusal: SupportUserSummaryWriteRefusalEnum | null;
  onSend: (payload: { text: string; file: File | null }) => void;
}

const MAX_TEXTAREA_HEIGHT = 120;

export const SupportComposer: FC<SupportComposerProps> = ({ canWrite, refusal, onSend }) => {
  const { t } = useLocalTranslation(translations);
  const isDesktop = useIsDesktop();
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const resize = () => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  };

  const pickFile = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    setIsMenuOpen(false);
    if (!picked) return;
    if (picked.size > SUPPORT_UPLOAD_LIMIT_BYTES) {
      setError(t('fileTooBig'));
      return;
    }
    setError(null);
    setFile(picked);
  };

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed && !file) return;
    onSend({ text: trimmed, file });
    setText('');
    setFile(null);
    setError(null);
    requestAnimationFrame(resize);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isDesktop) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  // Someone who has not paid yet is not shut out — say what opens the chat,
  // and say it where the composer would be, before anything is typed.
  if (!canWrite) {
    return (
      <div className="shrink-0 border-t border-separator-non-opaque px-4 pb-[max(14px,var(--tg-viewport-safe-area-inset-bottom,0px))] pt-3 text-center text-[13px] text-label-tertiary">
        {t(refusal === SupportUserSummaryWriteRefusalEnum.PaymentRequired ? 'needsPayment' : 'blocked')}
      </div>
    );
  }

  return (
    <div className="shrink-0 border-t border-separator-non-opaque bg-bg-secondary px-3 pb-[max(10px,var(--tg-viewport-safe-area-inset-bottom,0px))] pt-2">
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={pickFile} className="hidden" />
      <input ref={galleryInputRef} type="file" accept="image/*" onChange={pickFile} className="hidden" />
      <input ref={fileInputRef} type="file" onChange={pickFile} className="hidden" />

      {file != null && (
        <div className="mb-2 flex items-center gap-2 rounded-xl bg-fill-tertiary px-2 py-2">
          {previewUrl != null ? (
            <img src={previewUrl} alt={file.name} className="h-10 w-10 rounded-lg object-cover" />
          ) : null}
          <span className="min-w-0 flex-1 truncate text-[13px] text-label-secondary">{file.name}</span>
          <button
            type="button"
            onClick={() => setFile(null)}
            aria-label={t('removeFile')}
            className="flex h-8 w-8 items-center justify-center rounded-full text-label-tertiary active:opacity-70"
          >
            <FiX size={14} />
          </button>
        </div>
      )}

      {error != null && <div className="mb-1 px-1 text-[12px] text-accent-red">{error}</div>}

      <div className="relative flex items-end gap-2">
        {isMenuOpen && (
          <>
            <div className="fixed inset-0 z-0" onClick={() => setIsMenuOpen(false)} />
            <div className="absolute bottom-12 left-0 z-10 flex w-44 flex-col overflow-hidden rounded-2xl bg-bg-tertiary shadow-[0_8px_24px_rgba(0,0,0,0.45)]">
              {!isDesktop && (
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-3 text-left text-[15px] text-label-primary active:bg-fill-tertiary"
                >
                  <LuCamera size={16} />
                  {t('attachCamera')}
                </button>
              )}
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="px-4 py-3 text-left text-[15px] text-label-primary active:bg-fill-tertiary"
              >
                {t('attachPhoto')}
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-3 text-left text-[15px] text-label-primary active:bg-fill-tertiary"
              >
                {t('attachFile')}
              </button>
            </div>
          </>
        )}

        <button
          type="button"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-label={t('attach')}
          className="z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-fill-tertiary text-label-secondary active:opacity-70"
        >
          <FaPlus size={16} />
        </button>

        <textarea
          ref={textareaRef}
          value={text}
          rows={1}
          placeholder={t('placeholder')}
          onChange={(event) => {
            setText(event.target.value);
            resize();
          }}
          onKeyDown={handleKeyDown}
          className="min-h-11 flex-1 resize-none rounded-2xl bg-fill-tertiary px-4 py-[11px] text-[15px] leading-tight text-label-primary outline-none placeholder:text-label-tertiary"
        />

        <button
          type="button"
          onClick={submit}
          disabled={!text.trim() && !file}
          aria-label={t('send')}
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-opacity',
            !text.trim() && !file
              ? 'bg-fill-tertiary text-label-tertiary'
              : 'bg-accent-blue text-white active:opacity-80',
          )}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
            <path
              d="M9 15V3.5M9 3.5L4 8.5M9 3.5l5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
